import {
  useStarConversationMutation,
  useGetConversationByIdQuery,
} from 'app/services/conversations';
import { IConversation } from 'app/services/messages';
import { IUser } from 'app/services/user';
import { PasswordProfileIcon, StarFillIcon, StarIcon } from 'assets/icons';
import { successToast } from 'Components/Notifications';
import { Link, useParams } from 'react-router-dom';
import useConversationId from '../../Hooks/useConversationId';
import { useConversationsList } from '../../Hooks/useConversationsList';
import { useAuth } from 'utils/AuthProvider';
import UserAvatarList from 'Components/UserAvatarList';
import ElapsedTimer from './ElapseTimer';

export interface ConversationPreviewProps {
  value: IConversation;
}

export default function ConversationThumbnail({
  value: conversation,
}: {
  value: IConversation;
}) {
  const { markAsRead, addConversation } = useConversationsList();
  const [starConversation] = useStarConversationMutation();
  const conversationId = useConversationId();
  const { clientId } = useParams();
  const { userId } = useAuth();

  // 1) Fetch full conversation details, including .users
  const { data: conversationDetail, isLoading: isLoadingDetail } =
    useGetConversationByIdQuery(conversation.id, {
      skip: !conversation.id,
    });

  // 2) Use the .users array from the full detail
  const allUsers = conversationDetail?.users || [];
  const hasUnread = !!conversation.last_unread_message;

  return (
    <Link
      to={conversation.id.toString()}
      onClick={() => markAsRead(conversation)}
    >
      <div className="cursor-pointer border-b border-gray-100 relative">
        <div
          className={`text-sm p-4 ${
            hasUnread ? 'bg-gray-light bg-opacity-10' : ''
          }`}
        >
          <div className="flex flex-col">
            {/* Title & top row */}
            <div className="flex items-center">
              <div className="flex-1 truncate">
                <div className="text-gray-dark font-extrabold truncate">
                  {conversation.title}
                </div>

                {/* If loading, show a small spinner or placeholder */}
                {isLoadingDetail && (
                  <div className="text-xs text-gray-400">Loading...</div>
                )}

                {/* Otherwise show all participants */}
                {!isLoadingDetail && allUsers.length > 0 && (
                  <div className="mt-1 text-xs text-gray-500 whitespace-normal md:truncate">
                    {allUsers
                      .map((u: IUser) => `${u.given_name} ${u.family_name}`)
                      .join(', ')}
                  </div>
                )}
              </div>

              {/* Star / Favorite button */}
              {!clientId && (
                <div
                  className="w-5 h-5 cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    starConversation({
                      conversationId: conversation.id,
                      setAsStarred: !conversation.stared_at,
                    })
                      .unwrap()
                      .then(addConversation)
                      .then(() => successToast('Conversation updated'));
                  }}
                >
                  {conversation.stared_at ? <StarFillIcon /> : <StarIcon />}
                </div>
              )}

              {/* Lock icon if client is not a member */}
              {clientId && !conversation.is_member && (
                <PasswordProfileIcon className="text-gray-700 w-5 h-5" />
              )}
            </div>

            {/* Date of last message */}
            {conversation.last_message && (
              <div className="mr-2 whitespace-nowrap text-gray-light text-xs">
                <ElapsedTimer date={conversation.last_message.created_at} />
              </div>
            )}
          </div>

          {/* Raw content of last message */}
          {conversation.last_message?.raw_content && (
            <div
              className="text-gray-dark text-opacity-70 mt-2 line-clamp-2 whitespace-pre-line ql-editor"
              dangerouslySetInnerHTML={{
                __html: conversation.last_message.raw_content,
              }}
            />
          )}

          {/* Display avatars of all users in conversation */}
          {!isLoadingDetail && allUsers.length > 0 && (
            <div className="mt-2">
              <UserAvatarList users={allUsers} />
            </div>
          )}
        </div>

        {/* Left highlight if this conversation is active */}
        {conversationId === conversation.id && (
          <div className="absolute w-1 bg-green left-0 top-0 h-full" />
        )}
      </div>
    </Link>
  );
}
